import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { reportError } from "@/lib/reportError";

const ParentAuthContext = createContext(null);

export function ParentAuthProvider({ children }) {
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/parent/me");
      setParent(data);
    } catch (err) {
      if (err?.response?.status !== 401) reportError("Parent session check failed", err);
      setParent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/parent/login", { email, password });
    setParent(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/parent/register", { name, email, password });
    setParent(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await api.post("/parent/logout");
    setParent(null);
  }, []);

  const value = useMemo(
    () => ({ parent, loading, login, register, logout, refresh }),
    [parent, loading, login, register, logout, refresh]
  );

  return <ParentAuthContext.Provider value={value}>{children}</ParentAuthContext.Provider>;
}

export function useParentAuth() {
  return useContext(ParentAuthContext);
}
